import { useCallback, useRef, useState } from 'react';

// Tracks a set of in-flight ids (e.g. photo slots or document types). The ref
// mirrors the state set so `beginProcessing`/`isProcessing` resolve
// synchronously, guarding against a double-submit within a single render before
// the state update flushes.
export const useProcessingSet = <Id>() => {
  const processingRef = useRef<Set<Id>>(new Set());
  const [processing, setProcessing] = useState<Set<Id>>(new Set());

  const isProcessing = useCallback((id: Id): boolean => processingRef.current.has(id), []);

  // Returns false if the id was already processing, so callers can bail early.
  const beginProcessing = useCallback((id: Id): boolean => {
    if (processingRef.current.has(id)) {
      return false;
    }
    const next = new Set(processingRef.current).add(id);
    processingRef.current = next;
    setProcessing(next);
    return true;
  }, []);

  const endProcessing = useCallback((id: Id) => {
    if (!processingRef.current.has(id)) {
      return;
    }
    const next = new Set(processingRef.current);
    next.delete(id);
    processingRef.current = next;
    setProcessing(next);
  }, []);

  return { processing, isProcessing, beginProcessing, endProcessing };
};
